import type { ElectronicIdResponseType } from 'src/components/Login/Form/types';
import { LoggerServiceFactory, type ILoggerService } from './LoggerService';

export type FetchPkiSimRequest = {
  phonenumber: string;
  url: string;
  xsrf: string;
  traceparent: string;
};
export type FetchAudkenniAppRequest = {
  kennitala: string;
  url: string;
  xsrf: string;
  traceparent: string;
};
export interface IElectronicIdService {
  fetchPkiSimResponse<T extends ElectronicIdResponseType>(
    request: FetchPkiSimRequest,
  ): Promise<T & { response: Response }>;
  fetchAudkenniAppResponse<T extends ElectronicIdResponseType>(
    request: FetchAudkenniAppRequest,
  ): Promise<T & { response: Response }>;
}

export class ElectronicIdService implements IElectronicIdService {
  private logger: ILoggerService;
  constructor() {
    this.logger = LoggerServiceFactory.create('ElectronicIdService');
  }

  private async fetchElectronicIdResponse<T extends ElectronicIdResponseType>(
    xsrf: string,
    traceparent: string,
    url: string,
    body: Record<string, unknown>,
  ): Promise<T & { response: Response }> {
    this.logger.trace('Posting to Electronic ID endpoint', {
      data: { url, body },
    });
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': xsrf,
        'X-Requested-With': 'XMLHttpRequest',
        traceparent,
      },
      body: JSON.stringify(body),
    });

    const t: T = await response.json();
    this.logger.trace('Received response from Electronic ID endpoint', {
      data: { responseStatus: response.status, responseBody: t },
    });
    return { response, ...t };
  }

  async fetchPkiSimResponse<T extends ElectronicIdResponseType>({
    phonenumber,
    url,
    traceparent,
    xsrf,
  }: FetchPkiSimRequest): Promise<T & { response: Response }> {
    return this.fetchElectronicIdResponse<T>(xsrf, traceparent, url, {
      phoneNumber: phonenumber,
    });
  }

  async fetchAudkenniAppResponse<T extends ElectronicIdResponseType>({
    kennitala,
    url,
    traceparent,
    xsrf,
  }: FetchAudkenniAppRequest): Promise<T & { response: Response }> {
    return this.fetchElectronicIdResponse<T>(xsrf, traceparent, url, {
      kennitala: kennitala,
    });
  }
}

export const ElectronicIdServiceToken = Symbol(ElectronicIdService.name);
